// Settings → Customer mails (customer-contact plan, phase 3a, 2026-10-15). backend/customer_mail.py,
// docs/CUSTOMER-MAILS.md. When a carrier refused a phone number or a name and nothing in our data repairs it,
// the customer is asked from the store's customer-service mailbox through the storefront's own alias, in the
// storefront's language; Nora Mikken's store manager gets a short internal mail instead. This screen: the
// switches (all off), the senders per storefront, the texts (English master → translations → approved one by
// one), and "who would be asked now" — the dry run. Nothing on this screen mails a customer except
// "Send test to me", which goes to the person pressing it.
import { useCallback, useEffect, useState } from 'react';
import { API, authFetch } from './api';

const CARD = 'bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4';
const F = 'px-2 py-1.5 rounded bg-[#0f1117] border border-gray-700 text-white text-sm focus:outline-none focus:border-blue-500';
const STATUS_CLS: Record<string, string> = {
  approved: 'bg-green-900/50 text-green-300', draft: 'bg-amber-900/50 text-amber-300',
  outdated: 'bg-orange-900/50 text-orange-300', missing: 'bg-gray-800 text-gray-500',
};

async function call(path: string, method = 'GET', body?: any) {
  const r = await authFetch(`${API}/customer-mails${path}`, { method, body: body ? JSON.stringify(body) : undefined });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
  return d;
}

export default function CustomerMailsTab() {
  const [ov, setOv] = useState<any>(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [plan, setPlan] = useState<any[] | null>(null);
  const [open, setOpen] = useState<{ kind: string; lang: string } | null>(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    call('/overview').then(d => { setOv(d); setErr(''); }).catch(e => setErr(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);
  const loadPlan = () => call('/plan').then(d => setPlan(d.rows)).catch(e => setErr(e.message));
  useEffect(() => { loadPlan(); }, []);
  // the background translation (backend customer_mail._job): poll while it runs, refresh the grid as languages land
  const [job, setJob] = useState<any>(null);
  const pollJob = useCallback(() => {
    let seen = -1;
    const tick = (): void => { call('/translate/status').then(j => {
      setJob(j);
      const n = (j.done || []).length;
      if (n !== seen) { seen = n; load(); }
      if (j.running) setTimeout(tick, 3000); else loadPlan();
    }).catch(() => { setTimeout(tick, 5000); }); };
    tick();
  }, [load]);
  useEffect(() => { call('/translate/status').then(j => { if (j.running) pollJob(); else if (j.langs) setJob(j); }).catch(() => {}); }, [pollJob]);

  if (err) return <div className="text-red-400 text-sm">{err}</div>;
  if (!ov) return <div className="text-gray-500 text-sm">Loading…</div>;
  if (!ov.ready) return <div className="text-amber-400 text-sm">The customer-mail tables are missing (migration 089).</div>;
  const edit = !!ov.can_edit;

  // every translated language waiting as a draft, in one go (Fernando 2026-10-15) — never the English master
  const approveAll = async () => {
    try {
      const d = await call('/texts/approve-all', 'POST', { dry: true });
      const name = (x: any) => `${ov.languages[x.lang] || x.lang} (${ov.kinds[x.kind]?.label})`;
      if (!d.ready.length) {
        setMsg(d.skipped.length ? `Nothing can be approved: ${d.skipped.map((x: any) => `${name(x)}: ${x.why}`).join('; ')}`
                                : '✓ No translation is waiting for approval.');
        return;
      }
      const langs = Array.from(new Set(d.ready.map((x: any) => ov.languages[x.lang] || x.lang))).join(', ');
      if (!window.confirm(`Approve ${d.ready.length} translated text set(s) — ${langs}? From then on they can be sent to customers.` +
        (d.skipped.length ? `\n\nSkipped (${d.skipped.length}): ${d.skipped.map((x: any) => `${name(x)} — ${x.why}`).join('; ')}` : '') +
        `\n\nThe English texts are not included — approve those one by one.`)) return;
      setBusy('approve_all'); setMsg('');
      const r = await call('/texts/approve-all', 'POST', {});
      setMsg(`✓ Approved ${r.approved.length} text set(s).${r.skipped.length ? ` Skipped ${r.skipped.length}: ` +
        r.skipped.map((x: any) => `${name(x)} — ${x.why}`).join('; ') : ''}`);
      load(); loadPlan();
    } catch (e: any) { setMsg(e.message); } finally { setBusy(''); }
  };

  const translateMissing = async () => {
    try {
      const d = await call('/translate', 'POST', { dry: true });
      if (d.running) { setMsg('A translation is already running — see its progress above the grid.'); pollJob(); return; }
      if (!d.calls) { setMsg('✓ Nothing to translate — every language is up to date with the approved English.'); return; }
      if (!window.confirm(`Translate the approved English into ${d.calls} language(s) (${d.langs.join(', ')})? ` +
        `One Claude API call each (about half a minute per language). The translations arrive as drafts — nothing is sent ` +
        `before you approve a language. You can leave this page; it runs on the server.`)) return;
      setMsg('');
      await call('/translate', 'POST', {});
      pollJob();
    } catch (e: any) { setMsg(e.message); }
  };

  return (
    <div className="space-y-5 max-w-6xl">
      <p className="text-gray-500 text-sm">
        When a carrier refuses a phone number or a name and the WMS cannot repair it, the customer is asked by e-mail from the
        store's customer-service mailbox — through the <span className="text-gray-300">storefront's own alias, in its language,
        with the alias's own signature</span> — a reminder the next day, another the day after, cancelled on day 3. Nora
        Mikken's store manager gets a short internal mail instead. <span className="text-gray-300">Nothing is sent while the
        switches below are off</span>; the list at the bottom shows who would be asked.
      </p>
      {msg && <div className={`text-xs ${msg.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>{msg}</div>}
      <SwitchesCard ov={ov} edit={edit} onSaved={() => { load(); loadPlan(); }} setMsg={setMsg} />
      <SendersCard ov={ov} edit={edit} onChanged={() => { load(); loadPlan(); }} setMsg={setMsg} />

      <div className={CARD}>
        <div className="flex items-center mb-3">
          <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold flex-1">Texts</div>
          {edit && <button onClick={approveAll} disabled={!!busy || !!job?.running}
            className="px-3 py-1.5 mr-2 bg-green-800 hover:bg-green-700 disabled:opacity-40 text-white text-xs rounded">
            {busy === 'approve_all' ? 'Approving…' : 'Approve all drafts…'}</button>}
          {edit && <button onClick={translateMissing} disabled={!!busy || !!job?.running}
            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white text-xs rounded">
            {job?.running ? 'Translating…' : 'Translate missing…'}</button>}
        </div>
        {job?.langs && (
          <div className={`text-xs mb-3 ${job.running ? 'text-sky-300' : Object.keys(job.failed || {}).length ? 'text-amber-400' : 'text-green-400'}`}>
            {job.running
              ? `Translating ${ov.languages[job.current] || job.current || '…'} — ${(job.done || []).length} of ${job.langs.length} done (started by ${job.by}).`
              : `Last translation (${job.by}): ${(job.done || []).length} of ${job.langs.length} languages done${job.finished_at ? `, finished ${new Date(job.finished_at + 'Z').toLocaleTimeString()}` : ''}.`}
            {Object.entries(job.failed || {}).map(([lg, why]: any) => <div key={lg}>✗ {ov.languages[lg] || lg}: {why}</div>)}
            {(job.done || []).filter((d: any) => d.problems?.length).map((d: any) => <div key={d.lang}>! {ov.languages[d.lang] || d.lang}: {d.problems.join('; ')}</div>)}
          </div>
        )}
        <p className="text-xs text-gray-500 mb-3">English is the master: approve it first, then translate, read each language
          (Check in English shows a literal back-translation) and approve it. Only approved texts are ever sent; a language
          made from an older English stays live until the new one is approved.</p>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead><tr className="text-gray-500"><th className="text-left pr-3 py-1">Mail</th>
              {Object.entries(ov.languages).map(([lg, name]: any) => <th key={lg} className="px-1 font-normal" title={name}>{lg}</th>)}</tr></thead>
            <tbody>
              {Object.entries(ov.kinds).map(([k, info]: any) => (
                <tr key={k} className="border-t border-gray-800">
                  <td className="pr-3 py-1.5 text-gray-300 whitespace-nowrap">{info.label}</td>
                  {Object.keys(ov.languages).map(lg => {
                    const c = ov.grid[k]?.[lg];
                    if (!c) return <td key={lg} />;
                    return (
                      <td key={lg} className="px-0.5">
                        <button onClick={() => setOpen({ kind: k, lang: lg })} title={`${ov.languages[lg]} — ${c.status}`}
                          className={`px-1.5 py-0.5 rounded ${STATUS_CLS[c.status] || ''}`}>
                          {c.status === 'approved' ? '✓' : c.status === 'draft' ? 'd' : c.status === 'outdated' ? '!' : '·'}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[11px] text-gray-500 mt-2">✓ approved · d draft waiting for approval · ! approved, but the English changed since · · missing</div>
      </div>

      <PlanCard rows={plan} onReload={loadPlan} />
      <RecentCard rows={ov.recent} />
      {open && <TextDialog kind={open.kind} lang={open.lang} ov={ov} edit={edit} onClose={() => { setOpen(null); load(); loadPlan(); }} />}
    </div>
  );
}

function SwitchesCard({ ov, edit, onSaved, setMsg }: any) {
  const [s, setS] = useState<any>(ov.settings);
  useEffect(() => setS(ov.settings), [ov.settings]);
  const store = (id: string) => s.stores[id] || {};
  const setStore = (id: string, patch: any) => setS({ ...s, stores: { ...s.stores, [id]: { ...store(id), ...patch } } });
  const save = async () => {
    try {
      const r = await call('/settings', 'PUT', s);
      setMsg(r.test_cases_ended
        ? `✓ Saved. ${r.test_cases_ended} test case(s) ended — on each order, Ask the customer now mails the real customer.`
        : '✓ Saved.');
      onSaved();
    } catch (e: any) { setMsg(e.message); }
  };
  return (
    <div className={CARD}>
      <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-3">Switches</div>
      <div className="text-xs mb-3">{ov.gmail.verified
        ? <span className="text-green-400">● Gmail connected ({ov.gmail.mailboxes.join(', ')})</span>
        : <span className="text-amber-400">● No verified Gmail connection — Settings → Connectors → Google Workspace</span>}</div>
      <label className="flex items-center gap-2 text-sm text-gray-200 mb-3">
        <input type="checkbox" checked={!!s.on} disabled={!edit} onChange={e => setS({ ...s, on: e.target.checked })} />
        Customer mails on (master switch — off = nothing is sent, whatever the stores say)
      </label>
      <table className="text-sm mb-3">
        <thead><tr className="text-gray-500 text-xs text-left"><th className="pr-4 py-1">Store</th><th className="pr-4">Ask for the phone</th>
          <th className="pr-4">Ask for the name</th><th className="pr-4">Cancel on day 3</th><th className="pr-4">Daily cap</th><th>Store manager instead (internal)</th></tr></thead>
        <tbody>
          {ov.stores.map((st: any) => {
            const c = store(st.id);
            const internal = st.platform === 'shopify' || c.internal;
            return (
              <tr key={st.id} className="border-t border-gray-800 align-top">
                <td className="pr-4 py-1.5 text-gray-300">{st.name}</td>
                <td className="pr-4">{!internal && <input type="checkbox" checked={!!c.phone} disabled={!edit} onChange={e => setStore(st.id, { phone: e.target.checked })} />}</td>
                <td className="pr-4">{!internal && <input type="checkbox" checked={!!c.name} disabled={!edit} onChange={e => setStore(st.id, { name: e.target.checked })} />}</td>
                <td className="pr-4">{!internal && <span title="Comes with the next step (3d); until then day 3 becomes a Review item">
                  <input type="checkbox" checked={false} disabled /> <span className="text-[11px] text-gray-500">later</span></span>}</td>
                <td className="pr-4">{!internal && <input type="number" min={0} max={150} value={c.daily_cap ?? 20} disabled={!edit}
                  onChange={e => setStore(st.id, { daily_cap: Number(e.target.value) })} className={`${F} w-20`} />}</td>
                <td>{internal && (
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 text-xs text-gray-300">
                      <input type="checkbox" checked={!!c.internal} disabled={!edit} onChange={e => setStore(st.id, { internal: e.target.checked })} />
                      mail the store manager (daily until fixed)</label>
                    <input value={(c.recipients || []).join(', ')} disabled={!edit} placeholder="manager@motleydenim.com"
                      onChange={e => setStore(st.id, { recipients: e.target.value.split(/[\s,;]+/).filter(Boolean) })} className={`${F} w-72`} />
                  </div>)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <label className="text-xs text-gray-500 block mb-3">Test recipient — when set, EVERY customer mail goes to this address instead
        (for the first live tests)
        <input value={s.test_recipient || ''} disabled={!edit} onChange={e => setS({ ...s, test_recipient: e.target.value })}
          placeholder="fernando.orgusaar@motleydenim.com" className={`${F} w-80 block mt-1`} /></label>
      {edit && <button onClick={save} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg">Save switches</button>}
      <p className="text-[11px] text-amber-300/80 mt-2">These switches send REAL e-mails: with the master switch, a store's switch and a
        sender all on, every fresh refusal (the last 3 days) of that storefront is asked within minutes. Set a test recipient first to see
        the mails yourself — test cases never reach the customer and close when the test recipient is cleared.</p>
    </div>
  );
}

function SendersCard({ ov, edit, onChanged, setMsg }: any) {
  const [busy, setBusy] = useState('');
  const put = async (r: any, patch: any) => {
    try { await call(`/routes/${r.webshop_id}/${encodeURIComponent(r.site_code)}`, 'PUT', patch); onChanged(); setMsg('✓ Saved.'); }
    catch (e: any) { setMsg(e.message); }
  };
  const test = async (r: any) => {
    setBusy(r.site_code);
    try {
      const d = await call(`/routes/${r.webshop_id}/${encodeURIComponent(r.site_code)}/test`, 'POST');
      setMsg(`✓ Test sent from ${d.from} to ${d.to}${d.in_sent ? ` — it is in ${d.mailbox}'s Sent` : ''}. Reply to it to check the reply reaches ${d.mailbox}.`);
      onChanged();
    } catch (e: any) { setMsg(e.message); } finally { setBusy(''); }
  };
  return (
    <div className={CARD}>
      <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-3">Senders — one per storefront</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-gray-500 text-left"><th className="py-1 pr-2">Store</th><th className="pr-2">Storefront</th><th className="pr-2">Language</th>
            <th className="pr-2">Sent as (alias)</th><th className="pr-2">Status</th><th className="pr-2">Test</th><th>Enabled</th></tr></thead>
          <tbody>
            {ov.routes.map((r: any) => {
              const al = ov.gmail.aliases?.[r.mailbox] || [];
              return (
                <tr key={r.webshop_id + r.site_code} className="border-t border-gray-800 text-gray-300">
                  <td className="py-1.5 pr-2">{r.store_name || r.webshop_id}</td>
                  <td className="pr-2">{r.domain} <span className="text-gray-500">({r.site_code})</span></td>
                  <td className="pr-2">{r.language}</td>
                  <td className="pr-2">
                    <select value={r.send_as} disabled={!edit} onChange={e => put(r, { send_as: e.target.value })} className={`${F} text-xs max-w-[260px]`}>
                      {!al.some((a: any) => a.email === r.send_as) && <option value={r.send_as}>{r.send_as} (not in Gmail's list)</option>}
                      {al.map((a: any) => <option key={a.email} value={a.email}>{a.email}{a.name ? ` — ${a.name}` : ''}</option>)}
                    </select>
                  </td>
                  <td className={`pr-2 ${r.ready ? 'text-green-400' : 'text-amber-400'}`} title={(r.problems || []).join('\n')}>
                    {r.ready ? '✓ ready' : (r.problems || [])[0]}</td>
                  <td className="pr-2 whitespace-nowrap">
                    {edit && <button onClick={() => test(r)} disabled={!!busy} className="px-2 py-1 bg-gray-800 hover:bg-gray-700 rounded">
                      {busy === r.site_code ? 'Sending…' : 'Send test to me'}</button>}
                    {r.test_sent_at && <span className="text-gray-500 ml-1" title={`by ${r.test_sent_by}`}>✓ {new Date(r.test_sent_at + 'Z').toLocaleDateString()}</span>}
                  </td>
                  <td><input type="checkbox" checked={!!r.enabled} disabled={!edit} onChange={e => put(r, { enabled: e.target.checked })} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-gray-500 mt-2">A sender can be enabled once its alias is a verified "Send mail as" of the mailbox and its
        language's phone texts are approved. "Send test to me" sends the first phone mail (sample values) from the alias to you —
        reply to it to prove replies land in the mailbox the WMS reads.</p>
    </div>
  );
}

function TextDialog({ kind, lang, ov, edit, onClose }: any) {
  const [d, setD] = useState<any>(null);
  const [form, setForm] = useState<Record<number, { subject: string; body: string }>>({});
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [prev, setPrev] = useState<any>(null);
  const [english, setEnglish] = useState<any>(null);
  const [order, setOrder] = useState('');
  const [busy, setBusy] = useState('');
  const load = useCallback(() => {
    call(`/texts/${kind}/${lang}`).then(x => {
      setD(x);
      const f: any = {};
      for (const s of x.steps) { const t = s.draft || s.live; f[s.step] = { subject: t?.subject || '', body: t?.body || '' }; }
      setForm(f);
    }).catch(e => setErr(e.message));
  }, [kind, lang]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Modal onClose={onClose}><div className="text-gray-500 text-sm">{err || 'Loading…'}</div></Modal>;

  const save = async () => {
    setBusy('save'); setMsg('');
    try {
      const r = await call(`/texts/${kind}/${lang}`, 'PUT', { steps: d.steps.map((s: any) => ({ step: s.step, ...form[s.step] })) });
      setMsg(r.changed ? `✓ Saved as a draft (${r.changed} changed).` : '✓ Nothing changed.'); load();
    } catch (e: any) { setMsg(e.message); } finally { setBusy(''); }
  };
  const approve = async () => {
    if (!window.confirm(`Approve the ${d.language} texts for "${ov.kinds[kind].label}"? From now on they can be sent.`)) return;
    setBusy('approve'); setMsg('');
    try { await call(`/texts/${kind}/${lang}/approve`, 'POST'); setMsg('✓ Approved.'); load(); }
    catch (e: any) { setMsg(e.message); } finally { setBusy(''); }
  };
  const preview = async (step: number) => {
    setBusy(`p${step}`); setMsg('');
    try { setPrev({ step, ...(await call('/preview', 'POST', { kind, step, lang, order: order.trim() || undefined })) }); }
    catch (e: any) { setMsg(e.message); } finally { setBusy(''); }
  };
  const check = async () => {
    setBusy('check'); setMsg('');
    try { setEnglish((await call(`/texts/${kind}/${lang}/check`, 'POST')).english); }
    catch (e: any) { setMsg(e.message); } finally { setBusy(''); }
  };
  const hasDraft = d.steps.some((s: any) => s.draft);
  return (
    <Modal onClose={onClose}>
      <div className="flex items-baseline gap-3 mb-1">
        <div className="text-white font-semibold">{ov.kinds[kind].label} — {d.language}</div>
        <span className={`text-xs px-2 py-0.5 rounded ${STATUS_CLS[d.status.status] || ''}`}>{d.status.status}</span>
      </div>
      <div className="text-xs text-gray-500 mb-3">Placeholders: {d.placeholders.map((p: string) => `{${p}}`).join(' ')}. The alias's Gmail
        signature is added below every customer mail — no sign-off here. Reminders reuse the first mail's subject.</div>
      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        {d.steps.map((s: any) => (
          <div key={s.step} className="border border-gray-800 rounded-lg p-3">
            <div className="flex items-center gap-2 text-xs mb-2">
              <span className="text-gray-300 font-semibold">{s.name}</span>
              {s.live && <span className="text-green-400">live v{s.live.version}</span>}
              {s.draft && <span className="text-amber-400">draft v{s.draft.version} ({s.draft.origin})</span>}
              <span className="flex-1" />
              <button onClick={() => preview(s.step)} disabled={!!busy} className="text-blue-400">{busy === `p${s.step}` ? '…' : 'Preview'}</button>
            </div>
            {lang !== 'en' && s.english && <div className="text-[11px] text-gray-500 mb-2 whitespace-pre-wrap">EN: {s.english.subject ? `${s.english.subject} — ` : ''}{s.english.body}</div>}
            {s.step === 0 && <input value={form[s.step]?.subject || ''} disabled={!edit} placeholder="Subject"
              onChange={e => setForm({ ...form, [s.step]: { ...form[s.step], subject: e.target.value } })} className={`${F} w-full mb-2`} />}
            <textarea value={form[s.step]?.body || ''} disabled={!edit} rows={6}
              onChange={e => setForm({ ...form, [s.step]: { ...form[s.step], body: e.target.value } })} className={`${F} w-full font-mono text-xs`} />
            {english && english[String(s.step)] && <div className="text-[11px] text-sky-300 mt-1 whitespace-pre-wrap">
              Back to English: {english[String(s.step)].subject ? `${english[String(s.step)].subject} — ` : ''}{english[String(s.step)].body}</div>}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {edit && <button onClick={save} disabled={!!busy} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded">Save draft</button>}
        {edit && hasDraft && <button onClick={approve} disabled={!!busy} className="px-3 py-1.5 bg-green-700 hover:bg-green-600 text-white text-sm rounded">Approve</button>}
        {edit && lang !== 'en' && <button onClick={check} disabled={!!busy} className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded">
          {busy === 'check' ? 'Checking…' : 'Check in English'}</button>}
        <input value={order} onChange={e => setOrder(e.target.value)} placeholder="preview with order no. (optional)" className={`${F} text-xs w-56`} />
        <span className="flex-1" />
        <button onClick={onClose} className="px-3 py-1.5 text-gray-400 text-sm">Close</button>
      </div>
      {msg && <div className={`text-xs mt-2 ${msg.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>{msg}</div>}
      {prev && (
        <div className="mt-3 border border-gray-700 rounded-lg p-3">
          <div className="text-xs text-gray-400">From: {prev.from_name} &lt;{prev.from_address}&gt;{prev.reply_to ? ` · Reply-To ${prev.reply_to}` : ''}</div>
          <div className="text-sm text-white mb-2">{prev.subject}</div>
          {prev.html
            ? <iframe title="preview" sandbox="" srcDoc={prev.html} className="w-full h-72 bg-white rounded" />
            : <pre className="text-xs text-gray-200 whitespace-pre-wrap">{prev.text}</pre>}
        </div>
      )}
    </Modal>
  );
}

function PlanCard({ rows, onReload }: { rows: any[] | null; onReload: () => void }) {
  return (
    <div className={CARD}>
      <div className="flex items-center mb-3">
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold flex-1">Who would be asked now (dry run — nothing is sent)</div>
        <button onClick={onReload} className="text-xs text-blue-400">refresh</button>
      </div>
      {!rows ? <div className="text-gray-500 text-sm">Loading…</div> : rows.length === 0
        ? <div className="text-gray-500 text-sm">No order waiting to be shipped was refused for its phone number or name.</div>
        : (
          <table className="w-full text-xs">
            <thead><tr className="text-gray-500 text-left"><th className="py-1 pr-2">Order</th><th className="pr-2">Mail</th><th className="pr-2">Given</th>
              <th className="pr-2">To</th><th className="pr-2">Storefront → sender</th><th className="pr-2">Language</th><th>Would send / blocked by</th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.order_id} className="border-t border-gray-800 text-gray-300 align-top">
                  <td className="py-1.5 pr-2">{r.order_number}<div className="text-gray-500">{r.store}</div></td>
                  <td className="pr-2">{r.kind_label}{r.audience === 'internal' ? <div className="text-gray-500">to the store manager</div>
                    : r.situation_name ? <div className="text-gray-500">order {r.situation_name}</div> : null}</td>
                  <td className="pr-2 font-mono">{r.value_given}{r.format_example ? <div className="text-gray-500">e.g. {r.format_example}</div> : null}</td>
                  <td className="pr-2">{(r.to || []).join(', ')}</td>
                  <td className="pr-2">{r.site || '—'}{r.sender ? <div className="text-gray-500">{r.sender}</div> : null}</td>
                  <td className="pr-2">{r.language || '—'}</td>
                  <td>{r.blockers.length
                    ? <ul className="text-amber-400 list-disc ml-4">{r.blockers.map((b: string) => <li key={b}>{b}</li>)}</ul>
                    : <span className="text-green-400">would send {new Date(r.would_send_at).toLocaleString()}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}

function RecentCard({ rows }: { rows: any[] }) {
  if (!rows?.length) return null;
  return (
    <div className={CARD}>
      <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-3">Last customer mails and tests</div>
      <table className="w-full text-xs">
        <thead><tr className="text-gray-500 text-left"><th className="py-1">When</th><th>Kind</th><th>From</th><th>To</th><th>Subject</th><th>Status</th></tr></thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.id} className="border-t border-gray-800 text-gray-300">
              <td className="py-1 whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</td>
              <td>{r.purpose}</td><td className="font-mono">{r.from_address}</td><td className="font-mono">{(r.to_addresses || []).join(', ')}</td>
              <td>{r.subject}</td>
              <td className={r.status === 'sent' ? 'text-green-400' : r.status === 'failed' ? 'text-red-400' : 'text-amber-400'} title={r.error || ''}>
                {r.status}{r.status === 'sent' && r.in_sent ? ' · in Sent' : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Modal({ children, onClose }: { children: any; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#14161f] border border-gray-700 rounded-xl p-5 w-full max-w-4xl mt-8" onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
